// src/components/invoiceTaxEdit/InvoiceTaxEdit.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Divider,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { toast } from "react-toastify";
import taxService from "services/TaxService";
import InvoiceTaxService from "services/InvoiceTaxService";
import { formatCurrency, formatNumber } from "utils/utils";

const InvoiceTaxEdit = ({
  tripId,
  type = "private",
  magicToken = "",
  onSaveSuccess,
  showEditButton = true,
  open,
  onClose,
}) => {
  const [invoiceDetails, setInvoiceDetails] = useState(null);
  const [taxList, setTaxList] = useState([]);
  const [loadingInvoice, setLoadingInvoice] = useState(false);
  const [saving, setSaving] = useState(false);

  // Controlled or uncontrolled modal state
  const [internalModalOpen, setInternalModalOpen] = useState(false);
  const isModalOpen = open !== undefined ? open : internalModalOpen;

  const [selectedCgstId, setSelectedCgstId] = useState("");
  const [selectedSgstId, setSelectedSgstId] = useState("");
  const [selectedIgstId, setSelectedIgstId] = useState("");

  const fetchInvoiceTaxes = async () => {
    if (!tripId && !magicToken) return;
    try {
      setLoadingInvoice(true);
      const res = await InvoiceTaxService.getInvoiceTaxes(
        tripId,
        type,
        magicToken,
      );
      if (res?.success && res?.data) {
        setInvoiceDetails(res.data);
      }
    } catch (err) {
      console.error("Failed to fetch invoice details", err);
    } finally {
      setLoadingInvoice(false);
    }
  };

  useEffect(() => {
    const fetchTaxMasters = async () => {
      try {
        const res = await taxService.getTaxList();
        if (res?.success) {
          setTaxList(res.data || []);
        }
      } catch (err) {
        console.error("Failed to fetch tax masters", err);
      }
    };
    fetchTaxMasters();
  }, []);

  useEffect(() => {
    fetchInvoiceTaxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripId, magicToken]);

  // Sync state selections when modal opens
  useEffect(() => {
    if (isModalOpen && invoiceDetails) {
      setSelectedCgstId(invoiceDetails.cgstTaxId || "");
      setSelectedSgstId(invoiceDetails.sgstTaxId || "");
      setSelectedIgstId(invoiceDetails.igstTaxId || "");
    }
  }, [isModalOpen, invoiceDetails]);

  const handleOpenModal = () => {
    setSelectedCgstId(invoiceDetails?.cgstTaxId || "");
    setSelectedSgstId(invoiceDetails?.sgstTaxId || "");
    setSelectedIgstId(invoiceDetails?.igstTaxId || "");
    if (open !== undefined) {
      // Handled by parent
    } else {
      setInternalModalOpen(true);
    }
  };

  const handleCloseModal = () => {
    if (onClose) {
      onClose();
    } else {
      setInternalModalOpen(false);
    }
  };

  const handleSaveTaxes = async () => {
    try {
      setSaving(true);
      const payload = {};
      const mode = invoiceDetails?.taxApplicationMode;
      if (mode === "cgst_sgst") {
        if (!selectedCgstId || !selectedSgstId) {
          toast.error(
            "Both CGST and SGST tax ids are required for intrastate tax",
          );
          setSaving(false);
          return;
        }
        payload.cgstTaxId = selectedCgstId;
        payload.sgstTaxId = selectedSgstId;
      } else if (mode === "igst") {
        if (!selectedIgstId) {
          toast.error("IGST tax id is required");
          setSaving(false);
          return;
        }
        payload.igstTaxId = selectedIgstId;
      } else {
        toast.error("Tax application mode is not configured for this trip");
        setSaving(false);
        return;
      }
      const activeTripId = tripId || invoiceDetails?.tripId;
      const res = await InvoiceTaxService.updateInvoiceTaxes(
        activeTripId,
        payload,
      );
      if (res?.success) {
        toast.success(res?.message || "Invoice taxes updated successfully");
        handleCloseModal();
        await fetchInvoiceTaxes();
        if (onSaveSuccess) {
          onSaveSuccess(res.data);
        }
      } else {
        toast.error(res?.message || "Failed to update invoice taxes");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update invoice taxes");
    } finally {
      setSaving(false);
    }
  };

  const cgstOptions = taxList.filter(
    (t) => t.isActive && t.name.toLowerCase().includes("cgst"),
  );
  const sgstOptions = taxList.filter(
    (t) => t.isActive && t.name.toLowerCase().includes("sgst"),
  );
  const igstOptions = taxList.filter(
    (t) => t.isActive && t.name.toLowerCase().includes("igst"),
  );
  const finalCgstOptions =
    cgstOptions.length > 0 ? cgstOptions : taxList.filter((t) => t.isActive);
  const finalSgstOptions =
    sgstOptions.length > 0 ? sgstOptions : taxList.filter((t) => t.isActive);
  const finalIgstOptions =
    igstOptions.length > 0 ? igstOptions : taxList.filter((t) => t.isActive);

  const previewGross = invoiceDetails?.grossAmount || 0;
  let previewCgstPct = 0;
  let previewSgstPct = 0;
  let previewIgstPct = 0;
  const mode = invoiceDetails?.taxApplicationMode;

  if (mode === "cgst_sgst") {
    const selC = taxList.find((t) => t.id === selectedCgstId);
    const selS = taxList.find((t) => t.id === selectedSgstId);
    previewCgstPct = selC
      ? parseFloat(selC.percentage)
      : (invoiceDetails?.cgstPercentage ?? 0);
    previewSgstPct = selS
      ? parseFloat(selS.percentage)
      : (invoiceDetails?.sgstPercentage ?? 0);
  } else if (mode === "igst") {
    const selI = taxList.find((t) => t.id === selectedIgstId);
    previewIgstPct = selI
      ? parseFloat(selI.percentage)
      : (invoiceDetails?.igstPercentage ?? 0);
  }

  const previewCgstAmt = previewGross * 0.01 * previewCgstPct;
  const previewSgstAmt = previewGross * 0.01 * previewSgstPct;
  const previewIgstAmt = previewGross * 0.01 * previewIgstPct;
  const previewGstAmt = previewCgstAmt + previewSgstAmt + previewIgstAmt;
  const previewTotal = previewGross + previewGstAmt;

  const buildCells = () => {
    if (!invoiceDetails) return [];
    if (mode === "cgst_sgst") {
      return [
        {
          label: "Gross Amount",
          value: "Rs. " + formatCurrency(invoiceDetails.grossAmount),
        },
        {
          label: "CGST Tax @" + invoiceDetails.cgstPercentage + "%",
          value: "Rs. " + formatNumber(invoiceDetails.cgstAmount),
        },
        {
          label: "SGST Tax @" + invoiceDetails.sgstPercentage + "%",
          value: "Rs. " + formatNumber(invoiceDetails.sgstAmount),
        },
        {
          label: "Total Tax Amt",
          value: "Rs. " + formatNumber(invoiceDetails.gstAmount),
        },
        {
          label: "Total Amount",
          value: "Rs. " + formatCurrency(invoiceDetails.totalAmount),
          bold: true,
        },
      ];
    }
    if (mode === "igst") {
      return [
        {
          label: "Gross Amount",
          value: "Rs. " + formatCurrency(invoiceDetails.grossAmount),
        },
        {
          label: "IGST Tax @" + invoiceDetails.igstPercentage + "%",
          value: "Rs. " + formatNumber(invoiceDetails.igstAmount),
        },
        {
          label: "Total Tax Amt",
          value: "Rs. " + formatNumber(invoiceDetails.gstAmount),
        },
        {
          label: "Total Amount",
          value: "Rs. " + formatCurrency(invoiceDetails.totalAmount),
          bold: true,
        },
      ];
    }
    return [
      {
        label: "Gross Amount",
        value: "Rs. " + formatCurrency(invoiceDetails.grossAmount || 0),
      },
      {
        label: "Total Tax Amt",
        value: "Rs. " + formatNumber(invoiceDetails.gstAmount || 0),
      },
      {
        label: "Total Amount",
        value: "Rs. " + formatCurrency(invoiceDetails.totalAmount || 0),
        bold: true,
      },
    ];
  };

  const cells = buildCells();

  return (
    <Box width="100%">
      {/* Read-only horizontal tax row */}
      {loadingInvoice ? (
        <Box display="flex" alignItems="center" justifyContent="center" py={1}>
          <CircularProgress size={18} />
        </Box>
      ) : invoiceDetails ? (
        <Box
          display="flex"
          flexDirection="row"
          alignItems="stretch"
          overflow="hidden"
          width="100%"
        >
          {cells.map((cell, idx) => (
            <React.Fragment key={idx}>
              <Box
                flex={1}
                px={1.0}
                py={1}
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                sx={{ minWidth: 0 }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: "#282828",
                    mb: 0.25,
                    textAlign: "center",
                    lineHeight: 1.2,
                    fontSize: "1rem",
                    fontWeight: cell.bold ? "bold" : "normal",
                  }}
                >
                  {cell.label}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#282828",
                    textAlign: "center",
                    whiteSpace: "nowrap",
                    fontWeight: cell.bold ? "bold" : "normal",
                  }}
                >
                  {cell.value}
                </Typography>
              </Box>
            </React.Fragment>
          ))}
        </Box>
      ) : null}

      {/* Edit Tax button */}
      {showEditButton && invoiceDetails && (
        <Box display="flex" justifyContent="flex-end" mt={1}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditIcon fontSize="small" />}
            onClick={handleOpenModal}
            sx={{
              borderColor: "#F3AF09",
              color: "#F3AF09",
              "&:hover": {
                borderColor: "#d49a00",
                color: "#d49a00",
                bgcolor: "#fffbea",
              },
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
            }}
          >
            Edit Tax
          </Button>
        </Box>
      )}

      {/* Edit Tax Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            fontSize: "1rem",
            borderBottom: "1px solid #f0f0f0",
            pb: 1.5,
          }}
        >
          Edit Invoice Tax
        </DialogTitle>

        <DialogContent sx={{ pt: 3, overflow: "visible", fontWeight: "600" }}>
          {mode === "cgst_sgst" ? (
            <Grid container spacing={2} mb={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="modal-cgst-label">CGST Tax</InputLabel>
                  <Select
                    labelId="modal-cgst-label"
                    value={selectedCgstId}
                    label="CGST Tax"
                    onChange={(e) => setSelectedCgstId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select CGST</em>
                    </MenuItem>
                    {finalCgstOptions.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name} ({t.percentage}%)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel id="modal-sgst-label">SGST Tax</InputLabel>
                  <Select
                    labelId="modal-sgst-label"
                    value={selectedSgstId}
                    label="SGST Tax"
                    onChange={(e) => setSelectedSgstId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select SGST</em>
                    </MenuItem>
                    {finalSgstOptions.map((t) => (
                      <MenuItem key={t.id} value={t.id}>
                        {t.name} ({t.percentage}%)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          ) : mode === "igst" ? (
            <Box mb={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="modal-igst-label">IGST Tax</InputLabel>
                <Select
                  labelId="modal-igst-label"
                  value={selectedIgstId}
                  label="IGST Tax"
                  onChange={(e) => setSelectedIgstId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select IGST</em>
                  </MenuItem>
                  {finalIgstOptions.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name} ({t.percentage}%)
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Typography variant="body2" color="error" mb={2}>
              Tax configuration mode missing for this trip.
            </Typography>
          )}

          <Box
            sx={{
              bgcolor: "#f9f9f9",
              borderRadius: "6px",
              border: "1px solid #e8e8e8",
              p: 1.5,
            }}
          >
            <Typography
              variant="caption"
              sx={{ color: "#888", fontWeight: 600, mb: 1, display: "block" }}
            >
              PREVIEW
            </Typography>
            <Grid container>
              <Grid item xs={7}>
                <Typography variant="body2" sx={{ color: "#555", py: 0.5 }}>
                  Gross Amount
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography
                  variant="body2"
                  sx={{ color: "#1a1a1a", py: 0.5, textAlign: "right" }}
                >
                  Rs. {formatCurrency(previewGross)}
                </Typography>
              </Grid>

              {mode === "cgst_sgst" ? (
                <>
                  <Grid item xs={7}>
                    <Typography variant="body2" sx={{ color: "#555", py: 0.5 }}>
                      CGST Tax ({previewCgstPct}%)
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#1a1a1a", py: 0.5, textAlign: "right" }}
                    >
                      Rs. {formatNumber(previewCgstAmt)}
                    </Typography>
                  </Grid>
                  <Grid item xs={7}>
                    <Typography variant="body2" sx={{ color: "#555", py: 0.5 }}>
                      SGST Tax ({previewSgstPct}%)
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#1a1a1a", py: 0.5, textAlign: "right" }}
                    >
                      Rs. {formatNumber(previewSgstAmt)}
                    </Typography>
                  </Grid>
                </>
              ) : mode === "igst" ? (
                <>
                  <Grid item xs={7}>
                    <Typography variant="body2" sx={{ color: "#555", py: 0.5 }}>
                      IGST Tax ({previewIgstPct}%)
                    </Typography>
                  </Grid>
                  <Grid item xs={5}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#1a1a1a", py: 0.5, textAlign: "right" }}
                    >
                      Rs. {formatNumber(previewIgstAmt)}
                    </Typography>
                  </Grid>
                </>
              ) : null}

              <Grid item xs={12}>
                <Divider sx={{ my: 0.75 }} />
              </Grid>

              <Grid item xs={7}>
                <Typography variant="body2" sx={{ color: "#555", py: 0.5 }}>
                  Total Tax Amt
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography
                  variant="body2"
                  sx={{ color: "#1a1a1a", py: 0.5, textAlign: "right" }}
                >
                  Rs. {formatNumber(previewGstAmt)}
                </Typography>
              </Grid>

              <Grid item xs={7}>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: "#1a1a1a", py: 0.5 }}
                >
                  Total Amount
                </Typography>
              </Grid>
              <Grid item xs={5}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: "#1a1a1a",
                    py: 0.5,
                    textAlign: "right",
                  }}
                >
                  Rs. {formatCurrency(previewTotal)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions
          sx={{ px: 3, pb: 2, pt: 1.5, borderTop: "1px solid #f0f0f0", gap: 1 }}
        >
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            disabled={saving}
            onClick={handleCloseModal}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            size="small"
            disabled={saving}
            onClick={handleSaveTaxes}
            sx={{
              backgroundColor: "#F3AF09",
              color: "#fff",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": { backgroundColor: "#d49a00" },
            }}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceTaxEdit;
